/**
 * Mobile header search — portal leading search to <body> (sticky-header safe).
 * Keep minimal: no body MutationObserver (caused scroll-lock loops / CPU spin).
 */
(() => {
  const LEADING_SEARCH = '.header__leading .header__search';
  const ICONS_SEARCH = '.header__icons .header__search';
  const OPEN_DETAILS =
    '.header__leading .header__search details[open], .header__icons .header__search details[open], .header__search.is-mobile-search-portal details[open]';
  const MOBILE_MQ = window.matchMedia('(max-width: 989px)');
  const PORTAL_CLASS = 'is-mobile-search-portal';
  const OVERFLOW_CLASSES = [
    'overflow-hidden',
    'overflow-hidden-mobile',
    'overflow-hidden-tablet',
    'overflow-hidden-desktop',
  ];

  const isSearchOpen = () => !!document.querySelector(OPEN_DETAILS);

  const unlockUnlessSearchOpen = () => {
    if (isSearchOpen()) return;
    document.documentElement.classList.remove('mobile-search-open');
    OVERFLOW_CLASSES.forEach((cls) => document.body.classList.remove(cls));
  };

  const syncSearchState = () => {
    const mobileOpen = isSearchOpen() && MOBILE_MQ.matches;
    document.documentElement.classList.toggle('mobile-search-open', mobileOpen);
    if (!mobileOpen) unlockUnlessSearchOpen();
  };

  const getSearchHost = () =>
    (MOBILE_MQ.matches
      ? document.querySelector('.header__leading')
      : document.querySelector('.header__icons')) ||
    document.querySelector('.header__leading') ||
    document.querySelector('.header__icons');

  const getActiveSearch = () => {
    const portaled = document.querySelector(`.header__search.${PORTAL_CLASS}`);
    if (portaled) return portaled;
    const leading = document.querySelector(LEADING_SEARCH);
    if (leading) return leading;
    return document.querySelector(ICONS_SEARCH);
  };

  const restorePortal = (searchRoot) => {
    if (!searchRoot?.classList.contains(PORTAL_CLASS)) return;
    const host = getSearchHost();
    if (!host) return;
    searchRoot.classList.remove(PORTAL_CLASS);
    host.appendChild(searchRoot);
  };

  const portalIfNeeded = (searchRoot) => {
    if (!MOBILE_MQ.matches || !searchRoot || searchRoot.classList.contains(PORTAL_CLASS)) return;
    searchRoot.classList.add(PORTAL_CLASS);
    document.body.appendChild(searchRoot);
  };

  const patchSearch = (searchRoot) => {
    if (!searchRoot || searchRoot.dataset.aycanSearchPortal === '1') return;
    searchRoot.dataset.aycanSearchPortal = '1';

    const details = searchRoot.querySelector('details');
    if (!details) return;

    const onToggle = () => {
      if (details.hasAttribute('open')) {
        portalIfNeeded(searchRoot);
      } else {
        restorePortal(searchRoot);
      }
      syncSearchState();
      if (!MOBILE_MQ.matches) {
        document.body.classList.remove('overflow-hidden');
      }
    };

    details.addEventListener('toggle', onToggle);

    const closeBtn = searchRoot.querySelector('.search-modal__close-button');
    const searchInput = searchRoot.querySelector('.search__input');
    if (closeBtn && searchInput && !closeBtn.dataset.aycanSearchCloseClear) {
      closeBtn.dataset.aycanSearchCloseClear = '1';
      closeBtn.addEventListener(
        'click',
        (event) => {
          // First tap with text → clear (keep panel open); empty → close.
          // Runs on every breakpoint so the single close-✕ doubles as clear.
          if (searchInput.value.trim()) {
            event.preventDefault();
            event.stopPropagation();
            searchInput.value = '';
            searchInput.dispatchEvent(new Event('input', { bubbles: true }));
            searchInput.focus();
          }
        },
        true
      );
    }

    onToggle();
  };

  // Toggle ONE class on the shared wrapper (.search-modal, parent of both the
  // dim overlay and the panel) when predictive results are open/loading. Both
  // descendants key off this single state, so the overlay's appearance and the
  // panel's pill→card morph are always in lockstep — no independent timing to
  // drift out of sync (reliable alternative to :has()).
  const syncResultsClass = (ps) => {
    const wrapper = ps.closest('.search-modal');
    if (!wrapper) return;
    const active = ps.hasAttribute('open') || ps.hasAttribute('loading');
    wrapper.classList.toggle('aycan-search--results', active);
  };

  const observeResults = (scope = document) => {
    scope.querySelectorAll('predictive-search').forEach((ps) => {
      if (ps.dataset.aycanResultsObserved === '1') return;
      ps.dataset.aycanResultsObserved = '1';
      const mo = new MutationObserver(() => syncResultsClass(ps));
      mo.observe(ps, { attributes: true, attributeFilter: ['open', 'loading'] });
      syncResultsClass(ps);
    });
  };

  const init = (scope = document) => {
    unlockUnlessSearchOpen();
    syncSearchState();
    observeResults(scope);

    const roots = scope.querySelectorAll(LEADING_SEARCH);
    if (roots.length) {
      roots.forEach(patchSearch);
      return;
    }

    scope.querySelectorAll(ICONS_SEARCH).forEach(patchSearch);
  };

  const onViewportChange = () => {
    const searchRoot = getActiveSearch();
    const details = searchRoot?.querySelector('details');
    if (!details) {
      unlockUnlessSearchOpen();
      return;
    }

    if (!MOBILE_MQ.matches) {
      restorePortal(searchRoot);
    } else if (details.hasAttribute('open')) {
      portalIfNeeded(searchRoot);
    }
    syncSearchState();
  };

  unlockUnlessSearchOpen();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  MOBILE_MQ.addEventListener('change', onViewportChange);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
